const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || "/api";

export type Movie = {
  id: number;
  title: string;
  titleTamil?: string;
  titleGerman?: string;
  year?: number;
  genre?: string;
  genreTamil?: string;
  genreGerman?: string;
  director?: string;
  directorTamil?: string;
  directorGerman?: string;
  cast?: string;
  castTamil?: string;
  castGerman?: string;
  description?: string;
  descriptionTamil?: string;
  descriptionGerman?: string;
  posterUrl?: string;
  trailerUrl?: string;
  rating?: number;
  duration?: number;
  language?: string;
  country?: string;
  releaseDate?: string;
  boxOffice?: string;
  awards?: string;
  awardsTamil?: string;
  awardsGerman?: string;
  isNewRelease?: boolean;
  isPopular?: boolean;
  isClassic?: boolean;
  tags?: string;
  tagsTamil?: string;
  tagsGerman?: string;
  createdAt?: string;
};

export type CinemaNews = {
  id: number;
  title: string;
  titleTamil?: string;
  titleGerman?: string;
  summary?: string;
  summaryTamil?: string;
  summaryGerman?: string;
  content?: string;
  contentTamil?: string;
  contentGerman?: string;
  imageUrl?: string;
  publishDate?: string;
  author?: string;
  category?: string;
  categoryTamil?: string;
  categoryGerman?: string;
  tags?: string;
  isBreaking?: boolean;
  isFeatured?: boolean;
  createdAt?: string;
};

export async function fetchAllMovies(): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies`);
    if (!res.ok) throw new Error("movies load failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getMovie(id: number): Promise<Movie | null> {
  try {
    const res = await fetch(`${API_BASE}/movies/${id}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function searchMovies(query: string): Promise<Movie[]> {
  try {
    const res = await fetch(
      `${API_BASE}/movies/search?q=${encodeURIComponent(query)}`
    );
    if (!res.ok) throw new Error("movie search failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getMoviesByGenre(genre: string): Promise<Movie[]> {
  try {
    const res = await fetch(
      `${API_BASE}/movies/genre/${encodeURIComponent(genre)}`
    );
    if (!res.ok) throw new Error("movies by genre failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getMoviesByYear(year: number): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/year/${year}`);
    if (!res.ok) throw new Error("movies by year failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getMoviesByRating(rating: number): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/rating/${rating}`);
    if (!res.ok) throw new Error("movies by rating failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getNewReleases(): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/new-releases`);
    if (!res.ok) throw new Error("new releases failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getPopularMovies(): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/popular`);
    if (!res.ok) throw new Error("popular movies failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getClassicMovies(): Promise<Movie[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/classics`);
    if (!res.ok) throw new Error("classic movies failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getGenres(): Promise<string[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/genres`);
    if (!res.ok) throw new Error("genres failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getYears(): Promise<number[]> {
  try {
    const res = await fetch(`${API_BASE}/movies/years`);
    if (!res.ok) throw new Error("years failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function fetchAllCinemaNews(): Promise<CinemaNews[]> {
  try {
    const res = await fetch(`${API_BASE}/cinema-news`);
    if (!res.ok) throw new Error("cinema news load failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getCinemaNews(id: number): Promise<CinemaNews | null> {
  try {
    const res = await fetch(`${API_BASE}/cinema-news/${id}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function searchCinemaNews(query: string): Promise<CinemaNews[]> {
  try {
    const res = await fetch(
      `${API_BASE}/cinema-news/search?q=${encodeURIComponent(query)}`
    );
    if (!res.ok) throw new Error("cinema news search failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getCinemaNewsByCategory(
  category: string
): Promise<CinemaNews[]> {
  try {
    const res = await fetch(
      `${API_BASE}/cinema-news/category/${encodeURIComponent(category)}`
    );
    if (!res.ok) throw new Error("cinema news by category failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getBreakingCinemaNews(): Promise<CinemaNews[]> {
  try {
    const res = await fetch(`${API_BASE}/cinema-news/breaking`);
    if (!res.ok) throw new Error("breaking cinema news failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getFeaturedCinemaNews(): Promise<CinemaNews[]> {
  try {
    const res = await fetch(`${API_BASE}/cinema-news/featured`);
    if (!res.ok) throw new Error("featured cinema news failed");
    return res.json();
  } catch {
    return [];
  }
}

export async function getCinemaNewsCategories(): Promise<string[]> {
  try {
    const res = await fetch(`${API_BASE}/cinema-news/categories`);
    if (!res.ok) throw new Error("cinema news categories failed");
    return res.json();
  } catch {
    return [];
  }
}
